import { useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  Search,
  ChevronDown,
  X,
  Check,
  FileText,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { invalidateOfferSheetCaches } from "@/lib/offer-sheet-cache";
import { BlockSkeleton } from "@/components/skeletons";

export function ClientSelector() {
  const { t } = useTranslation();
  const { clients, isAgencyUser, selectedClient, selectedClientId, setSelectedClient } =
    useAgencyClients();

  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editingClient, setEditingClient] = useState<(typeof clients)[number] | null>(null);

  if (!isAgencyUser || clients.length === 0) return null;

  const filteredClients = search
    ? clients.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
      )
    : clients;

  const handleSelect = (client: (typeof clients)[number]) => {
    const isDeselecting = selectedClientId === client.id;
    setSelectedClient(
      isDeselecting ? null : { id: client.id, userId: client.userId, name: client.name }
    );
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedClient(null);
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          <button className="flex items-center gap-2 px-3 py-2 w-full bg-white/[0.04] border border-white/[0.08] text-white text-xs rounded-xl hover:bg-white/[0.08] transition-colors group">
            <div className="w-6 h-6 rounded-lg bg-[#ef3e29]/20 flex items-center justify-center shrink-0">
              <Users className="w-3 h-3 text-[#ef3e29]" />
            </div>
            <span className="truncate flex-1 text-left">
              {selectedClient ? selectedClient.name : t("clientSelector.placeholder")}
            </span>
            {selectedClient ? (
              <X
                className="w-3 h-3 text-white/30 hover:text-white/60 shrink-0"
                onClick={handleClear}
              />
            ) : (
              <ChevronDown className="w-3 h-3 text-white/30 shrink-0" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0 bg-popover border-border" align="start" sideOffset={6}>
          {/* Search */}
          <div className="p-2 border-b border-white/[0.08]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <Input
                placeholder={t("clientSelector.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 bg-white/[0.06] border-white/[0.12] text-white text-xs placeholder:text-white/30"
                autoFocus
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredClients.map((c) => {
              const isActive = selectedClientId === c.id;
              return (
                <div
                  key={c.id}
                  className={`group/row w-full text-left px-3 py-2 text-sm transition-colors hover:bg-white/[0.08] flex items-center gap-2 ${
                    isActive ? "text-white bg-white/[0.05]" : "text-white/70"
                  }`}
                >
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => handleSelect(c)}
                  >
                    <div className="font-medium text-xs truncate">{c.name}</div>
                    {c.email && <div className="text-[10px] text-white/40 truncate">{c.email}</div>}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingClient(c);
                    }}
                    title={t("clientSelector.editOfferSheet")}
                    className={`shrink-0 p-1 rounded transition-colors ${
                      c.offerSheetId
                        ? "text-[#ef3e29]/70 hover:text-[#ef3e29] hover:bg-white/[0.06]"
                        : "text-white/30 hover:text-white/70 hover:bg-white/[0.06] opacity-0 group-hover/row:opacity-100"
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                  </button>
                  {isActive && <Check className="w-3.5 h-3.5 text-[#ef3e29] shrink-0" />}
                </div>
              );
            })}
            {filteredClients.length === 0 && (
              <div className="px-3 py-4 text-xs text-white/30 text-center">
                {t("clientSelector.noResults")}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* VP-99: offer-sheet warning lives here now (global source of truth), not inline in creators.
          Clickable → opens the "Arkusz idealnego klienta" (offer-builder) so the user can create one. */}
      {selectedClientId && !clients.find((c) => c.id === selectedClientId)?.offerSheetId && (
        <button
          type="button"
          onClick={() => setLocation("/chat/offer-builder")}
          title={t("clientSelector.noOfferSheetHint", {
            defaultValue: "Kliknij, aby stworzyć arkusz idealnego klienta",
          })}
          className="flex items-center gap-1.5 mt-1.5 px-2 py-1 w-full text-left bg-[#ef3e29]/10 hover:bg-[#ef3e29]/20 border border-[#ef3e29]/20 hover:border-[#ef3e29]/40 rounded-lg transition-colors"
        >
          <AlertTriangle className="w-3 h-3 text-[#ef3e29] shrink-0" />
          <span className="text-[10px] text-[#ef3e29]/85 leading-tight">
            {t("clientSelector.noOfferSheet", {
              defaultValue: "Brak arkusza ofertowego dla tego klienta",
            })}
          </span>
        </button>
      )}

      {editingClient && (
        <OfferSheetEditDialog client={editingClient} onClose={() => setEditingClient(null)} />
      )}
    </>
  );
}

interface OfferSheetEditDialogProps {
  client: { id: string; name: string; offerSheetId?: string | null };
  onClose: () => void;
}

function OfferSheetEditDialog({ client, onClose }: OfferSheetEditDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [value, setValue] = useState<string>(client.offerSheetId || "none");

  const { data: sheets, isLoading: sheetsLoading } = useQuery({
    queryKey: ["offer-sheets-picker"],
    queryFn: () => apiRequest("GET", "/api/offer-sheets"),
    staleTime: 300_000,
  });

  const offerSheets: { id: string; title: string; isActive: boolean }[] =
    (sheets as { id: string; title: string; isActive: boolean }[] | undefined) || [];

  const saveMutation = useMutation({
    mutationFn: () =>
      apiRequest("PUT", `/api/agency/clients/${client.id}`, {
        offerSheetId: value !== "none" ? value : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-clients"] });
      qc.invalidateQueries({ queryKey: ["/api/agency/clients"] });
      // VP-94: zmiana podpiętego arkusza klienta → odśwież podgląd arkusza w kreatorze.
      invalidateOfferSheetCaches(qc, client.id);
      toast({
        title: t("clientSelector.offerSheetSaved", {
          defaultValue: "Arkusz ofertowy zaktualizowany",
        }),
      });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: t("clientSelector.offerSheetSaveFailed", { defaultValue: "Nie udało się zapisać" }),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md bg-popover border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <FileText className="w-5 h-5 text-[#ef3e29]/70" />
            {t("clientSelector.editOfferSheetTitle", { defaultValue: "Arkusz ofertowy" })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-white/50">
            {t("clientSelector.editOfferSheetClient", { defaultValue: "Klient" })}:{" "}
            <span className="text-white">{client.name}</span>
          </div>
          {sheetsLoading ? (
            // VP-208: placeholder w wymiarach SelectTrigger (h-9), żeby pole
            // nie zmieniło wysokości po załadowaniu listy arkuszy.
            <BlockSkeleton className="h-9 w-full" />
          ) : (
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger className="bg-white/[0.06] border-white/[0.12] text-white h-9">
                <SelectValue
                  placeholder={t("clientSelector.offerSheetPlaceholder", {
                    defaultValue: "Wybierz arkusz...",
                  })}
                />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="none" className="text-white hover:bg-white/[0.08]">
                  <span className="text-white/50">
                    {t("clientSelector.offerSheetNone", { defaultValue: "Brak arkusza" })}
                  </span>
                </SelectItem>
                {offerSheets.map((s) => (
                  <SelectItem key={s.id} value={s.id} className="text-white hover:bg-white/[0.08]">
                    <span className="flex items-center gap-2">
                      <FileText className="w-3 h-3 text-[#ef3e29]/70" />
                      {s.title}
                      {s.isActive && <span className="text-[9px] text-green-400">●</span>}
                    </span>
                  </SelectItem>
                ))}
                {offerSheets.length === 0 && (
                  <div className="px-3 py-2 text-xs text-white/30">
                    {t("clientSelector.offerSheetEmpty", {
                      defaultValue: "Brak arkuszy. Stwórz w Arkuszach ofertowych.",
                    })}
                  </div>
                )}
              </SelectContent>
            </Select>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={saveMutation.isPending}
            className="text-white/70 bg-transparent border-white/[0.12]"
          >
            {t("common.cancel", { defaultValue: "Anuluj" })}
          </Button>
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || sheetsLoading}
            className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />{" "}
                {t("common.saving", { defaultValue: "Zapisuję..." })}
              </>
            ) : (
              t("common.save", { defaultValue: "Zapisz" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
